import { ReactElement, ReactNode, useRef } from 'react';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import { BeatLoader } from 'react-spinners';
import Header from '../Header';
import { downloadFile } from '../../_helpers/dashboardHelper';

type DataOverviewContainerProps = {
  children: ReactNode;
  sectionName: string;
  onZoom?: () => void;
  size?: 'full' | '1/2' | '2/3' | '1/3' | '2/4';
  zoomed?: boolean;
  onBackPress?: () => void;
  proSubscription?: boolean;
  noDataText?: string | boolean;
  loading: boolean;
  /** Optional controls rendered in the header, left of the camera/zoom buttons. */
  headerActions?: ReactNode;
};

/**
 * React component for DataOverviewContainer.
 * @param props - Properties of DataOverviewContainer component.
 * @returns - React component for DataOverviewContainer.
 */
export default function DataOverviewContainer(props: DataOverviewContainerProps): ReactElement {
  const {
    children,
    sectionName,
    onZoom,
    size,
    zoomed,
    onBackPress,
    proSubscription,
    noDataText,
    loading,
    headerActions
  } = props;
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Function to getWidth of component according to size.
   * @returns - Tailwind classname.
   */
  const getSize = (): string => {
    switch (size) {
      case 'full':
        return 'min-w-full flex-1';
      case '1/2':
        return 'w-full md:w-[49%] min-h-[22.5rem] md:h-[40vh]';
      case '1/3':
        return 'w-full md:w-[33%] min-h-[22.5rem] md:h-[39vh] overflow-hidden';
      case '2/4':
        return 'w-full md:w-[33%] min-h-[22.5rem] md:h-[40vh] overflow-x-auto';
      default:
        return 'flex-1';
    }
  };

  /**
   * Function to handle screen capture.
   */
  const handleScreenCapture = async (): Promise<void> => {
    const canvas = await html2canvas(ref.current as HTMLDivElement);
    const dataURL = canvas.toDataURL('image/png');
    downloadFile(dataURL, `${sectionName}_image.png`, 'image/png');
    toast.success('Image downloaded successfully');
  };

  return (
    <div className={`bg-white dark:bg-container-500 rounded p-4 my-2 flex flex-col ${getSize()}`}>
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <Header
            proSubscription={proSubscription}
            onBackPress={onBackPress}
            showBack={zoomed}
            sectionName={sectionName}
            showIcon
          />
        </div>
        <div className="flex items-center gap-3 h-">
          {headerActions}
          <button
            type="button"
            onClick={handleScreenCapture}
            className="material-icons-outlined text-gray-500"
          >
            photo_camera
          </button>
          {onZoom && (
            <button
              type="button"
              onClick={onZoom}
              className="material-icons-outlined text-gray-500"
            >
              fullscreen
            </button>
          )}
        </div>
      </div>
      <div
        ref={ref}
        className={
          size === 'full'
            ? 'flex flex-row justify-between w-full flex-1'
            : 'flex flex-col flex-1 overflow-hidden'
        }
      >
        {loading && (
          <div className="h-full w-full flex justify-center items-center">
            <BeatLoader color="#36d7b7" />
          </div>
        )}
        {!loading &&
          (!noDataText ? (
            children
          ) : (
            <div className="h-full w-full flex justify-center items-center">
              <p className="text-gray-500 text-center mt-8">{noDataText}</p>
            </div>
          ))}
      </div>
    </div>
  );
}
